import {Button, Group} from '@mantine/core';
import {useCallback, useMemo} from 'react';
import {SubmitHandler} from 'react-hook-form';
import {Link, useNavigate} from 'react-router-dom';
import {useTypedParams} from 'react-router-typesafe-routes/dom';
import {useAsync} from 'react-use';
import {CodesService} from '../api/generated/CodesService';
import {CodeClassTypes} from '../api/generated/enums.ts';
import {AsyncStateContainer} from '../components/async-state-container.tsx';
import {BasicPage} from '../components/basic-page';
import {BreadcrumbPart} from '../components/page-title.tsx';
import {createForm} from '../forms';
import {useNotification} from '../hooks/use-notifications.tsx';
import {ROUTES} from '../routes';
import {codeSchema, CodeSchema} from './schemas.ts';
import _ from 'lodash';

const CodeClassOptions = _.remove(
  Object.keys(CodeClassTypes),
  (x) => x !== CodeClassTypes['Level of Service'],
);

const breadcrumbs: BreadcrumbPart[] = [
  {title: 'Codes', href: ROUTES.ADMIN.CODES.INDEX.path},
  {title: 'Create'},
];

const Form = createForm<CodeSchema>();

const codeDefaultValues: CodeSchema = {
  description: '',
  codeClass: '',
  sortOrder: 0,
  inactive: false,
};

export const CodeCreate = () => {
  const navigate = useNavigate();
  const notifications = useNotification();

  const onSubmit = useCallback<SubmitHandler<CodeSchema>>(
    async (value) => {
      const response = await CodesService.create({body: value});

      if (response.hasErrors) {
        return response;
      }

      notifications.success('Code Created');
      navigate(ROUTES.ADMIN.CODES.INDEX.path);
    },
    [navigate, notifications],
  );

  return (
    <BasicPage title={breadcrumbs}>
      <Form
        defaultValues={codeDefaultValues}
        schema={codeSchema}
        onSubmit={onSubmit}
      >
        <Form.Container>
          <FormFields />
          <Group mt={24} justify={'flex-end'}>
            <Button
              type='button'
              variant='outline'
              component={Link}
              to={ROUTES.ADMIN.CODES.INDEX.path}
            >
              Cancel
            </Button>
            <Form.Submit>Create Code</Form.Submit>
          </Group>
        </Form.Container>
      </Form>
    </BasicPage>
  );
};

export const CodeUpdate = () => {
  const navigate = useNavigate();
  const notifications = useNotification();
  const {id} = useTypedParams(ROUTES.ADMIN.CODES.DETAIL);

  const fetchCode = useAsync(async () => {
    const {data} = await CodesService.getById({
      id,
    });
    return data;
  }, [id]);

  const code = fetchCode.value || undefined;
  const codeName = code?.description ?? '';

  const breadcrumbs = useMemo<BreadcrumbPart[]>(
    () => [
      {title: 'Codes', href: ROUTES.ADMIN.CODES.INDEX.path},
      {title: codeName},
    ],
    [codeName],
  );

  const onSubmit = useCallback<SubmitHandler<CodeSchema>>(
    async (value) => {
      const response = await CodesService.update({id, body: value});

      if (response.hasErrors) {
        return response;
      }

      notifications.success('Code Updated');
      navigate(ROUTES.ADMIN.CODES.INDEX.path);
    },
    [id, navigate, notifications],
  );

  return (
    <BasicPage title={breadcrumbs}>
      <AsyncStateContainer {...fetchCode}>
        <Form schema={codeSchema} onSubmit={onSubmit} defaultValues={code}>
          <Form.Container>
            <FormFields />
            <Group mt={24} justify={'flex-end'}>
              <Button
                type='button'
                variant='outline'
                component={Link}
                to={ROUTES.ADMIN.CODES.INDEX.path}
              >
                Cancel
              </Button>
              <Form.Submit>Update Code</Form.Submit>
            </Group>
          </Form.Container>
        </Form>
      </AsyncStateContainer>
    </BasicPage>
  );
};

const FormFields = () => {
  return (
    <Form.Row>
      <Form.TextInput
        name='description'
        label='Description'
        placeholder='Description'
      />
      <Form.Select
        name='codeClass'
        label='Class'
        placeholder='Class'
        enumObject={CodeClassOptions}
        searchable
      />
      <Form.NumberInput
        name='sortOrder'
        label='Sort Order'
        placeholder='Sort Order'
      />
      <Form.Checkbox name='inactive' label='Inactive' />
    </Form.Row>
  );
};
